import React, { FC, useEffect, useState } from 'react';
import { Autocomplete, Avatar, Box, Paper, TextField } from '@mui/material';
import { StorefrontOutlined } from '@mui/icons-material';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from '../store';
import { Shop } from '../types/shop.model';

const ShopAutocomplete: FC = () => {
  const { shopId } = useParams<{ shopId: string }>();
  const shops = useSelector<Shop[]>((state) => state.authentication.shops);

  const [value, setValue] = useState<Shop | null>(null);

  useEffect(() => {
    if (shopId) {
      const currentShop = shops.find((shop) => shop.id === +shopId);
      setValue(currentShop || null);
    }
  }, [shopId, shops]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Avatar
        alt={value?.name}
        src={value?.logoUri || undefined}
        sx={{
          width: 40,
          height: 40,
          bgcolor: 'orange.500',
          ...(value?.logoUri && {
            border: '2px solid',
            borderColor: 'grey.300'
          })
        }}
      >
        {!value?.logoUri && <StorefrontOutlined />}
      </Avatar>
      <Autocomplete
        PaperComponent={(props) => (
          <Paper
            {...props}
            sx={{
              minWidth: { xs: '100%', sm: 300 }
            }}
          />
        )}
        disableClearable={true}
        filterOptions={(options, { inputValue }) =>
          options.filter((shop) => shop.name.toLowerCase().includes(inputValue.toLowerCase()))
        }
        getOptionLabel={(shop) => shop.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText="Aucun commerce"
        onChange={(_, newValue) => {
          setValue(newValue as Shop | null);
        }}
        options={shops}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth={true}
            placeholder="Choix du commerce"
            sx={{
              '&:hover': {
                backgroundColor: 'action.hover'
              },
              borderRadius: '6px',
              input: {
                fontSize: '1.2rem',
                fontWeight: (theme) => theme.typography.fontWeightBold,
                lineHeight: '24px',
                whiteSpace: 'nowrap',
                p: '8px !important'
              },
              'input:not(:placeholder-shown)': {
                color: 'background.default'
              },
              '& .MuiInput-underline:before': {
                borderBottomColor: 'background.default'
              },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                borderBottomColor: 'background.default'
              },
              '& .MuiInput-underline:after': {
                borderBottomColor: 'background.default'
              }
            }}
            variant="standard"
          />
        )}
        renderOption={(props, option) => (
          <Box
            component={Link}
            to={`/${option.id}/overview`}
            {...(props as any)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 1,
              textDecoration: 'none',
              color: 'inherit',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <Avatar
              alt={option.name}
              src={option.logoUri || undefined}
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'orange.500',
                ...(option.logoUri && {
                  border: '1px solid',
                  borderColor: 'grey.300'
                })
              }}
            >
              {!option.logoUri && <StorefrontOutlined sx={{ fontSize: 16 }} />}
            </Avatar>
            {option.name}
          </Box>
        )}
        sx={{
          flex: 1
        }}
        value={value}
      />
    </Box>
  );
};

export default ShopAutocomplete;
